import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { apiAxios } from "../cms/api/mockApi";

const url = "/scms/api/pop/selectPopList.do";
const body = JSON.stringify({ viewYn: "Y", useAt: "Y" });

// =========================
// 정책 상수
// =========================
const POPUP_POLICY = {
  DEFAULT_WIDTH: 600,
  DEFAULT_HEIGHT: 520,
  MARGIN_RIGHT: 50,
  MARGIN_BOTTOM: 30,
};

// =========================
// 유틸
// =========================
const todayYmd = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const toYmd = (s) => (s || "").slice(0, 10);

// "520", "520px", "  520  " → 숫자 파싱, 실패 시 d 반환
const num = (v, d = 0) => {
  if (v == null) return d;
  const m = String(v).match(/-?\d+(\.\d+)?/);
  if (!m) return d;
  const n = Number(m[0]);
  return Number.isFinite(n) ? n : d;
};

const KEY_PREFIX = "popup.v2.hide.";
const storageKey = (p, idx = 0) => {
  const uniq = p.ID ?? p.CONT_SNO ?? p.SEQ ?? idx;
  return `${KEY_PREFIX}${p.CONT_ID}.${uniq}.${toYmd(p.VIEW_ST) || "NA"}.${todayYmd()}`;
};

// 새창 내부 안전 CSS (이미지/테이블 넘침 방지)
const SAFE_STYLE = `
  <style>
    html, body { margin:0; padding:0; box-sizing: border-box; }
    *, *::before, *::after { box-sizing: inherit; }
    img { max-width: 100%; height: auto; }
    table { max-width: 100%; }
  </style>
`;

// 화면 기준으로 창 크기 클램프
const clampWindowSize = (w, h) => {
  const availW = window.screen?.availWidth || window.innerWidth || 1280;
  const availH = window.screen?.availHeight || window.innerHeight || 800;
  const maxW = Math.max(1, availW - POPUP_POLICY.MARGIN_RIGHT);
  const maxH = Math.max(1, availH - POPUP_POLICY.MARGIN_BOTTOM);
  return { w: Math.min(w, maxW), h: Math.min(h, maxH) };
};

// 여러 새 창 열기 시도 (사용자 제스처 기반 재시도용)
function tryOpenWindows(items) {
  let opened = 0;
  items.forEach((p, i) => {
    const w = Number(p.SIZE_WIDTH) || 600;
    const h = Number(p.SIZE_HEIGHT) || 520;
    const left = 80 + i * 30, top = 80 + i * 30;
    const name = `popup_${p.ID || i}_${Date.now()}`;
    const feat = `width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes`;
    try {
      const win = window.open("about:blank", name, feat);
      if (win) {
        opened++;
        try {
          win.document.title = p.PMG_NM || "공지";
          win.document.body.innerHTML = `
            ${SAFE_STYLE}
            <div style="padding:12px; font-family:Arial,Helvetica,sans-serif">${p.PMG_CN || ""}</div>`;
          win.focus();
        } catch {}
      }
    } catch {}
  });
  return opened;
}

export default function PopupBoard() {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // 레이어(=W가 아닌 팝업)용
  const [openIds, setOpenIds] = useState([]);

  // ★ 주소창 아래 배너 알림용 상태 (차단된 W 팝업들)
  const [blockedWindows, setBlockedWindows] = useState([]);
  const [showBar, setShowBar] = useState(false);

  const qs = useMemo(() => new URLSearchParams(window.location.search), []);
  const popupReset = qs.has("popupReset");
  const DEBUG = qs.has("popupDebug");
  

  // 스토리지 초기화
  useEffect(() => {
    if (!popupReset) return;
    Object.keys(localStorage).forEach((k) => {
      if (k.startsWith("popup.hide.") || k.startsWith("popup.v2.hide.")) {
        localStorage.removeItem(k);
      }
    });
    Object.keys(sessionStorage).forEach((k) => {
      if (k.startsWith("popup.window.opened.")) sessionStorage.removeItem(k);
    });
    console.log("[PopupBoard] storage reset completed.");
  }, [popupReset]);

  // 데이터 로드
  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);
    setItems(null);

    apiAxios(url, body, (res) => {
      if (!mounted) return;
      if (res.apiRes === "Y") {
        setItems(res.list || []);
      } else {
        console.error("[PopupBoard] api error:", res.message);
        setError(new Error(res.message || "Popup API error"));
        setItems([]);
      }
      setLoading(false);
    });

    return () => {
      mounted = false;
    };
  }, []);

  // 활성 팝업 필터
  const active = useMemo(() => {
    const now = todayYmd();
    const src = items || [];
    const out = src.filter((p) => {
      const st = toYmd(p.VIEW_ST);
      const ed = toYmd(p.VIEW_ED);
      const use = String(p.USE_AT || "").trim().toUpperCase() === "Y";
      const view = String(p.VIEW_YN || "").trim().toUpperCase() === "Y";
      const notHidden = DEBUG ? true : !localStorage.getItem(storageKey(p));
      const inRange = (!st || st <= now) && (!ed || now <= ed);
      return use && view && inRange && notHidden;
    });
    if (DEBUG) console.log("[PopupBoard] active:", out);
    return out;
  }, [items, DEBUG]);

  // =========================
  // 새창 팝업 (WIN_STYLE === 'W') → 차단 시 배너 알림
  // =========================
  useEffect(() => {
    const windows = active.filter(
      (p) => String(p.WIN_STYLE || "").toUpperCase() === "W"
    );
    //console.log("[PopupBoard] active total:", active.length, " / active W:", windows.length);

    if (windows.length === 0) return;

    const GAP = 20;
    const BASE_LEFT = 950;
    const BASE_TOP = 150;

    const availW = (window.screen?.availWidth ?? window.innerWidth ?? 1280);
    const availH = (window.screen?.availHeight ?? window.innerHeight ?? 800);
    const availLeft = window.screen?.availLeft ?? 0;
    const availTop  = window.screen?.availTop ?? 0;

    const sizes = windows.map((p, i) => {
      const wRaw = Number.isFinite(num(p.SIZE_WIDTH, NaN)) ? num(p.SIZE_WIDTH) : POPUP_POLICY.DEFAULT_WIDTH;
      const hRaw = Number.isFinite(num(p.SIZE_HEIGHT, NaN)) ? num(p.SIZE_HEIGHT) : POPUP_POLICY.DEFAULT_HEIGHT;
      const { w, h } = clampWindowSize(wRaw, hRaw);
      return { w, h, p, i };
    });

    const colWidth = Math.max(...sizes.map(s => s.w)) + GAP;
    const cols = Math.max(1, Math.floor((availW - POPUP_POLICY.MARGIN_RIGHT - BASE_LEFT) / colWidth));

    const tilePos = (idx, w, h) => {
      const col = idx % cols;
      const row = Math.floor(idx / cols);
      let left = availLeft + BASE_LEFT + col * (colWidth);
      let top  = availTop  + BASE_TOP  + row * (h + GAP);
      left = Math.min(Math.max(left, availLeft), availLeft + availW - w - 10);
      top  = Math.min(Math.max(top,  availTop ), availTop  + availH - h - 10);
      return { left, top };
    };

    const openSequential = async () => {
      const blocked = [];
      for (let k = 0; k < sizes.length; k++) {
        const { w, h, p, i } = sizes[k];
        const { left, top } = tilePos(i, w, h);

        const name = `${p.CONT_ID || "cont"}_${p.ID || p.SEQ || i}_${Date.now()}_${Math.random().toString(36).slice(2,6)}`;
        const feat = `width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes`;

        const win = window.open("about:blank", name, feat);

        if (win) {
          try { win.resizeTo(w, h); win.moveTo(left, top); } catch {}
          win.document.title = p.PMG_NM || "공지";
          win.document.body.innerHTML = `
            ${SAFE_STYLE}
            <div style="font-family: system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial; line-height:1.5">
              <div style="padding:12px; white-space: pre-line; overflow:auto; max-height:${h - 96}px">
                ${p.PMG_CN || ""}
              </div>
              <div style="display:flex; align-items:center; justify-content:space-between; padding:8px 12px; border-top:1px solid #eef2f7; background:#fafafa;">
                <label style="font-size:12px; display:flex; gap:6px; align-items:center;">
                  <input type="checkbox" id="hideToday"/> 오늘 하루 보지 않기
                </label>
                <button id="okBtn" style="padding:6px 10px;">닫기</button>
              </div>
            </div>`;
          setTimeout(() => { try { win.resizeTo(w, h); win.moveTo(left, top); win.focus(); } catch {} }, 10);
          const okBtn = win.document.getElementById("okBtn");
          if (okBtn) okBtn.onclick = () => {
            const hide = win.document.getElementById("hideToday")?.checked;
            if (hide) localStorage.setItem(storageKey(p, i), "1");
            win.close();
          };
        } else {
          // ★ 차단: 배너 알림으로만 처리(레이어 폴백 없음)
          blocked.push(p);
        }

        await new Promise(r => setTimeout(r, 300));
      }

      if (blocked.length > 0) {
        setBlockedWindows(blocked);
        setShowBar(true);

        // 사용자 첫 제스처에서 자동 재시도 1회 (버튼 누르지 않아도 자연스럽게)
        const handler = () => {
          const reopened = tryOpenWindows(blocked);
          if (reopened > 0) setShowBar(false);
          window.removeEventListener("pointerdown", handler);
          window.removeEventListener("keydown", handler);
          window.removeEventListener("wheel", handler);
        };
        window.addEventListener("pointerdown", handler, { once: true });
        window.addEventListener("keydown", handler, { once: true });
        window.addEventListener("wheel", handler,   { once: true });
      } else {
        setShowBar(false);
      }
    };

    openSequential();
  }, [active]);

  // =========================
  // 레이어: (W가 아닌 것)만
  // =========================
  const layerItems = useMemo(() => {
    return active.filter((p) => String(p.WIN_STYLE || "").toUpperCase() !== "W");
  }, [active]);

  useEffect(() => {
    setOpenIds(layerItems.map((p) => p.CONT_ID));
  }, [layerItems]);

  if (loading || error || !items) return null;

  // 배너의 '공지 열기' 버튼
  const onOpenFromBar = () => {
    const reopened = tryOpenWindows(blockedWindows);
    if (reopened > 0) setShowBar(false);
  };

  return (
    <>
      {/* 레이어 팝업들 (W가 아닌 케이스만) */}
      {layerItems.map((p, idx) =>
        openIds.includes(p.CONT_ID) ? (
          <LayerPopup
            key={`${p.CONT_ID || "popup"}-${idx}`}
            pop={p}
            index={idx}
            onClose={() =>
              setOpenIds((ids) => ids.filter((ID) => ID !== p.CONT_ID))
            }
            onHideToday={() => localStorage.setItem(storageKey(p, idx), "1")}
          />
        ) : null
      )}

      {/* ★ 주소창 바로 아래 슬림 배너 (차단 알림) */}
      {showBar && (
        <div
          role="alert"
          aria-live="assertive"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 12000,
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "10px 16px",
            background: "rgba(229, 241, 255, 1)",          // amber-100 느낌
            // borderBottom: "1px solid #4d7cfcff", 
            boxShadow: "0 2px 10px rgba(0,0,0,.06)",
            fontFamily: "Noto Sans, Arial, Helvetica, sans-serif",
          }}
        >
          <span style={{ fontWeight: 700, color: "#0e113dff" /* amber-900 */ }}>
            팝업이 차단되었습니다.
          </span>
          <span style={{ fontSize: 13, color: "#0e113dff", flex: 1, minWidth: 0 }}>
            공지를 보시려면 ‘공지 열기’를 클릭하세요. (주소창 오른쪽 사이트 설정 → “팝업 및 리디렉션” 허용 시 자동으로 열립니다)
          </span>
          <button
            onClick={() => setShowBar(false)}
            style={{
              border: "none",
              background: "transparent",
              padding: "6px 8px",
              cursor: "pointer",
              color: "#0e113dff",
            }}
            aria-label="배너 닫기"
            title="닫기"
          >
            닫기
          </button>
          <button
            className="popupBtn"
            onClick={onOpenFromBar}
            style={{
              marginLeft: 4,
              border: "none",
              background: "#0b3dc5ff",
              color: "#fff",
              padding: "8px 12px",
              borderRadius: 6,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            공지 열기
          </button>
        </div>
      )}
    </>
  );
}

// =========================
// 레이어 팝업 (W가 아닌 것만)
// =========================
function LayerPopup({ pop, index = 0, onClose, onHideToday }) {
  const [noToday, setNoToday] = useState(false);

  // 1) 컬럼값 → 기본값 → 화면 상한만 적용
  const innerW = window.innerWidth || 1280;
  const innerH = window.innerHeight || 800;
  const rawW = Number.isFinite(num(pop.SIZE_WIDTH, NaN))  ? num(pop.SIZE_WIDTH)  : POPUP_POLICY.DEFAULT_WIDTH;
  const rawH = Number.isFinite(num(pop.SIZE_HEIGHT, NaN)) ? num(pop.SIZE_HEIGHT) : POPUP_POLICY.DEFAULT_HEIGHT;

  const W = Math.min(rawW, Math.max(1, innerW - POPUP_POLICY.MARGIN_RIGHT));
  const H = Math.min(rawH, Math.max(1, innerH - POPUP_POLICY.MARGIN_BOTTOM));

  // 2) 좌표 = 둘 다 있을 때 사용 → viewport 경계로 클램프, 없으면 타일
  const GAP = 20;
  const BASE_LEFT = 40;
  const BASE_TOP = 60;
  const cols = Math.max(1, Math.floor((innerW - 40) / (W + GAP)));
  const col = index % cols;
  const row = Math.floor(index / cols);
  const leftTile = BASE_LEFT + col * (W + GAP);
  const topTile  = BASE_TOP + row * (H + GAP);

  const lNum = num(pop.POS_LEFT, NaN);
  const tNum = num(pop.POS_TOP, NaN);
  const leftRaw = Number.isFinite(lNum) ? lNum : leftTile;
  const topRaw  = Number.isFinite(tNum) ? tNum : topTile;

  const left = Math.min(Math.max(leftRaw, 0), Math.max(0, innerW - W - 10));
  const top  = Math.min(Math.max(topRaw, 0), Math.max(0, innerH - H - 10));

  const wrapStyle = {
    position: "fixed",
    left, top,
    width: W,
    height: H,
    background: "#fff",
    borderRadius: 10,
    border: "1px solid #e5e7eb",
    boxShadow: "0 18px 48px rgba(0,0,0,.25)",
    overflow: "hidden",
    zIndex: 9999,
  };

  const contentStyle = { overflow: "auto", height: "calc(100% - 96px)" };

  return createPortal(
    <div style={wrapStyle} role="dialog" aria-label="공지 팝업" aria-modal="true">
      <div
        style={{
          padding: "10px 14px",
          fontWeight: 700,
          borderBottom: "1px solid #eef2f7",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 8,
        }}
      >
        <span className="text-truncate">{pop.PMG_NM || "공지"}</span>
        <button className="btn btn-sm btn-outline-secondary" onClick={onClose} aria-label="팝업 닫기">
          닫기
        </button>
      </div>

      <div
        className="p-3"
        style={contentStyle}
        dangerouslySetInnerHTML={{
          __html: `<style>img{max-width:100%;height:auto}table{max-width:100%}</style>` + (pop.PMG_CN || ""),
        }}
      />

      <div
        className="d-flex align-items-center justify-content-between p-2"
        style={{ borderTop: "1px solid #eef2f7", background: "#fafafa" }}
      >
        <label className="d-flex align-items-center gap-2 small m-0">
          <input
            type="checkbox"
            checked={noToday}
            onChange={(e) => setNoToday(e.target.checked)}
          />
          오늘 하루 보지 않기
        </label>
        <button
          className="btn btn-sm btn-primary"
          onClick={() => {
            if (noToday) onHideToday();
            onClose();
          }}
        >
          확인
        </button>
      </div>
    </div>,
    document.body
  );
}
